/**
 * Client-side sanitizers for money text inputs (product prices, PV, points…).
 *
 * The server's `moneyStringSchema` accepts `/^-?\d+(\.\d{1,4})?$/`; these
 * helpers guarantee a field can only ever hold a value that schema accepts
 * (or the empty string — blank means "inherit"/"not sold" on several product
 * fields, so it must never be coerced to "0"). Negatives are deliberately
 * not typeable: every product-form amount is non-negative.
 */

export type MoneySanitizeOpts = {
  /** Digits only — for quantity-like fields (stock qty). */
  integer?: boolean;
};

/**
 * Keystroke/paste sanitizer: strips everything outside [0-9.], keeps only the
 * first dot, and caps the fraction at 4 digits. "1,000" → "1000",
 * "12.34.56" → "12.3456", "₱1 500.5" → "1500.5".
 */
export function sanitizeMoneyInput(raw: string, opts: MoneySanitizeOpts = {}): string {
  const s = raw.replace(/[^0-9.]/g, '');
  if (opts.integer) return s.replace(/\./g, '');
  const dot = s.indexOf('.');
  if (dot === -1) return s;
  const frac = s.slice(dot + 1).replace(/\./g, '').slice(0, 4);
  return `${s.slice(0, dot)}.${frac}`;
}

/**
 * Blur normalizer on top of {@link sanitizeMoneyInput}: ".2" → "0.2" (the
 * server regex requires an integer part) and "5." → "5". Empty stays empty.
 */
export function normalizeMoneyOnBlur(value: string, opts: MoneySanitizeOpts = {}): string {
  let s = sanitizeMoneyInput(value, opts);
  if (s === '') return '';
  if (s.startsWith('.')) s = `0${s}`;
  if (s.endsWith('.')) s = s.slice(0, -1);
  return s;
}
